"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import { AppProviders } from "@/components/AppProviders";
import { useAuth } from "@/components/AuthProvider";
import { OrgAccessSplash } from "@/components/OrgAccessSplash";
import { useUrlOrgAccessGate } from "@/hooks/useUrlOrgAccessGate";
import { DashboardShellFallback } from "@/app/DashboardShellFallback";
import { DashboardRouterProvider, useDashboardRouter } from "@/hooks/useDashboardRouter";
import { OrgUrlSyncProvider } from "@/components/OrgUrlSyncProvider";
import { SidebarLayout } from "@/components/SidebarLayout";
import { DesktopLayoutSkeleton } from "@/components/desktop/DesktopLayoutSkeleton";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { githubLogin } from "@/lib/github-login";

// Lazy, as in `_app.tsx`, so web visitors never download the desktop chrome.
const DesktopSidebarLayout = dynamic(() => import("@/components/desktop/DesktopSidebarLayout").then(mod => mod.DesktopSidebarLayout), {
  loading: () => <DesktopLayoutSkeleton />
});
const DesktopDragRegion = dynamic(() => import("@/components/desktop/DesktopLayoutSkeleton").then(mod => mod.DesktopDragRegion));

// The Chrome extension iframe (`?embed=extension`) renders the page bare, as
// `_app.tsx` does: no rail, sidebars, command palette, shortcuts modal, or
// the shell's prefetch hooks. The page still needs a Suspense boundary below
// AppProviders: pages that suspend at their root (the PR resolver reads
// useActiveGithubOrgForUser there) would otherwise bubble to the layout
// boundary, discarding the query client and auth tree and re-suspending on
// the fresh cache.
function DashboardShell(t0) {
  const $ = _c(16);
  const {
    children
  } = t0;
  const router = useDashboardRouter();
  const {
    user,
    logout
  } = useAuth();
  const orgAccess = useUrlOrgAccessGate();
  const surface = useCodesmithSurface(router.query.embed);
  if (orgAccess.state === "pending") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DashboardShellFallback />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (orgAccess.state === "denied") {
    let t1;
    if ($[1] !== surface) {
      t1 = surface === "desktop" && <DesktopDragRegion />;
      $[1] = surface;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const t2 = orgAccess.org;
    const t3 = orgAccess.fallbackOrg;
    let t4;
    if ($[3] !== user) {
      t4 = githubLogin(user);
      $[3] = user;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== logout || $[6] !== orgAccess.fallbackOrg || $[7] !== orgAccess.org || $[8] !== t4) {
      t5 = <OrgAccessSplash org={t2} fallbackOrg={t3} username={t4} onLogout={logout} />;
      $[5] = logout;
      $[6] = orgAccess.fallbackOrg;
      $[7] = orgAccess.org;
      $[8] = t4;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t1 || $[11] !== t5) {
      t6 = <>{t1}{t5}</>;
      $[10] = t1;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t1;
  if ($[13] !== children || $[14] !== surface) {
    t1 = <OrgUrlSyncProvider>{surface === "extension" ? <Suspense fallback={<DashboardShellFallback />}>{children}</Suspense> : surface === "desktop" ? <DesktopSidebarLayout>{children}</DesktopSidebarLayout> : <SidebarLayout>{children}</SidebarLayout>}</OrgUrlSyncProvider>;
    $[13] = children;
    $[14] = surface;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  return t1;
}
export default function DashboardLayout(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DashboardShellFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><DashboardRouterProvider><AppProviders><DashboardShell>{children}</DashboardShell></AppProviders></DashboardRouterProvider></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}